import { Link } from 'react-router-dom';
import type { Topic } from '@/types/content';
import { topics } from '@/mocks/topics';
import { formatHeat } from '@/utils/format';
import SectionHeading from '@/components/feature/SectionHeading';
import Reveal from '@/components/feature/Reveal';

const allTopics = topics as Topic[];

export default function HotTopics() {
  const featured = allTopics[0];
  const ranked = allTopics.slice(1, 6);

  return (
    <section id="hot-topics" className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <SectionHeading
          eyebrow="Hot now"
          title="热门瓜点速览"
          description="按照吃瓜群众当下的关注热度排序，先看最热闹的那几个。"
        />
        <Link
          to="/frontline"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap"
        >
          查看全部瓜点
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
        <Reveal className="lg:col-span-2">
          <article className="relative h-full overflow-hidden rounded-lg bg-foreground-950">
            <Link to={`/topic/${featured.id}`} className="block w-full h-full">
              <div className="w-full h-64 md:h-[420px] overflow-hidden">
                <img
                  src={featured.image}
                  alt={`${featured.title}配图`}
                  title={`${featured.title} · 511群众热门瓜点`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                  <i className="ri-fire-line"></i>
                  热度最高
                </span>
                <h3 className="mt-3 font-heading font-bold text-xl md:text-3xl text-white leading-snug">
                  {featured.title}
                </h3>
                <p className="mt-2 text-sm text-background-200 line-clamp-2 max-w-2xl">{featured.summary}</p>
                <div className="mt-3 flex items-center gap-4 text-xs text-background-300">
                  <span>{featured.categoryLabel}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line"></i>
                    {formatHeat(featured.heat)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {featured.comments}
                  </span>
                </div>
              </div>
            </Link>
          </article>
        </Reveal>

        <Reveal delay={120}>
          <div className="h-full rounded-lg border border-background-200/80 bg-background-100/60 p-4 md:p-5">
            <h3 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 mb-4">
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-accent-950 text-xs">
                <i className="ri-bar-chart-2-line"></i>
              </span>
              热度榜单
            </h3>
            <ol className="space-y-1">
              {ranked.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/topic/${item.id}`}
                    className="flex items-start gap-3 py-3 border-b border-background-200/70 last:border-0 group"
                  >
                    <span
                      className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold ${
                        index === 0
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {index + 2}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                        {item.title}
                      </span>
                      <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <span>{item.categoryLabel}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-primary-500"></i>
                          {formatHeat(item.heat)}
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
      </div>
    </section>
  );
}